import React, {
  useState,
  useEffect,
  Dispatch,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Stack,
  Button,
  Divider,
  Tooltip,
  Fab,
  MenuItem,
  Typography,
  Select,
  Pagination,
  Grid,
} from "@mui/material";
import {
  LicenseInfo,
  GridColDef,
  GridColumnMenuPinningItem,
  GridColumnMenuSortItem,
  GridColumnMenuItemProps,
  GridColumnMenuProps,
  DataGridPro,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  ToolbarPropsOverrides,
  GridRowId,
  GridRowModel,
} from "@mui/x-data-grid-pro";
import { FilterAlt, Search, Clear, RefreshRounded } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { I18n } from "utilities/utilities";
import {
  DataGridProp,
  FilterFieldInterface,
} from "components/DataGrid/DataGridProps";
import { FormProvider, useForm } from "react-hook-form";

import { BtnExportExcel, BtnDelete } from "components/Button/ButtonAll";
import moment from "moment";
import { TextBoxItem } from "components/Input/TextBox";
import { SelectItem } from "components/Input/Select/SelectItem";
import { MultipleSelectItem } from "components/Input/Select/MultipleSelectItem";
import { DateRangePickerItem } from "components/Input/DatePicker/DateRangePickerItem";
import { DatePickerItem } from "components/Input/DatePicker/DatePickerItem";
import { I18NextNs } from "enum/enum";

LicenseInfo.setLicenseKey(
  "049fdd25559575ef04f65a9e1ed5aabaTz02NDk0MCxFPTE3MTM2NzY2MjYyNzIsUz1wcm8sTE09cGVycGV0dWFsLEtWPTI="
);

// #region No Data
const StyledGridOverlay = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  "& .ant-empty-img-1": {
    fill: theme.palette.mode === "light" ? "#aeb8c2" : "#262626",
  },
  "& .ant-empty-img-2": {
    fill: theme.palette.mode === "light" ? "#f5f5f7" : "#595959",
  },
  "& .ant-empty-img-3": {
    fill: theme.palette.mode === "light" ? "#dce0e6" : "#434343",
  },
  "& .ant-empty-img-4": {
    fill: theme.palette.mode === "light" ? "#fff" : "#1c1c1c",
  },
  "& .ant-empty-img-5": {
    fillOpacity: theme.palette.mode === "light" ? "0.8" : "0.08",
    fill: theme.palette.mode === "light" ? "#f5f5f5" : "#fff",
  },
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <svg
        width="120"
        height="100"
        viewBox="0 0 184 152"
        aria-hidden
        focusable="false"
      >
        <g fill="none" fillRule="evenodd">
          <g transform="translate(24 31.67)">
            <ellipse
              className="ant-empty-img-5"
              cx="67.797"
              cy="106.89"
              rx="67.797"
              ry="12.668"
            />
            <path
              className="ant-empty-img-1"
              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
            />
            <path
              className="ant-empty-img-2"
              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
            />
            <path
              className="ant-empty-img-3"
              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
            />
          </g>
          <path
            className="ant-empty-img-3"
            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
          />
          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
          </g>
        </g>
      </svg>
      <Box sx={{ mt: 1 }}>ไม่พบข้อมูล</Box>
    </StyledGridOverlay>
  );
}
// #endregion
// #region Filter
declare module "@mui/x-data-grid-pro" {
  interface FilterPanelPropsOverrides {
    isRow: boolean;
    filterField: FilterFieldInterface[];
    colums: GridColDef[];
    isLoading: boolean;
    sTableID: string;
  }
  interface ToolbarPropsOverrides {
    isShowColomnTool: boolean;
    isExportExcel: boolean;
    isFilter: boolean;
    isRow: boolean;
    filterField: FilterFieldInterface[];
    colums: GridColDef[];
    isLoading: boolean;
    sTableID: string;
    isShowFilter: boolean;
    setShowFilter: Dispatch<React.SetStateAction<boolean>>;
  }
}
const ControlFilter = (
  item: FilterFieldInterface,
  colums: GridColDef[],
  isLoading,
  sTableID
) => {
  let sLabelFilter = item.sLabel;
  if (!item.sLabel) {
    const objColumns = colums.find((f) => f.field === item.sFieldName);
    if (objColumns) {
      sLabelFilter = objColumns.headerName;
    }
  }
  let sIDFilter = `filter_${sTableID}_${item.sFieldName}`;
  let arrOption = item.optionSelect ? item.optionSelect : [];

  switch (item.sTypeFilterMode) {
    case "input": {
      return (
        <TextBoxItem
          id={sIDFilter}
          disabled={isLoading}
          label={sLabelFilter}
          name={sIDFilter}
          type={"text"}
          maxLength={200}
          required={false}
          style={{ margin: "0" }}
        />
      );
    }
    case "select": {
      return (
        <SelectItem
          required={false}
          label={sLabelFilter}
          id={sIDFilter}
          name={sIDFilter}
          options={arrOption}
        />
      );
    }
    case "multiselect": {
      return (
        <MultipleSelectItem
          required={false}
          label={sLabelFilter}
          id={sIDFilter}
          name={sIDFilter}
          options={arrOption}
          limitTag={1}
        />
      );
    }
    case "daterange": {
      return (
        <DateRangePickerItem
          name={sIDFilter}
          id={sIDFilter}
          label={sLabelFilter}
          labelStart={`${item.sLabel} ${I18n.SetText("start")}`}
          labelEnd={`${item.sLabel} ${I18n.SetText("end")}`}
          minDate={item.minDate ? item.minDate : null}
          maxDate={item.maxDate ? item.maxDate : null}
        />
      );
    }
    case "date": {
      return (
        <DatePickerItem
          name={sIDFilter}
          id={sIDFilter}
          label={sLabelFilter}
          fullWidth
        />
      );
    }
  }
};
const GridToolbarFilterCustomButton = styled(Button)(({ theme }) => ({
  fontSize: "0.8125rem",
  padding: "4px 5px",
}));

// #endregion
// #endregion

export default function DataGrid(props: DataGridProp) {
  const [lstDefaultSorting, setDefaultSorting] = useState(null);
  const [selectionModel, setSelectionModel] = React.useState<GridRowId[]>(
    props.arrSelect ?? []
  );
  useEffect(() => {
    setSelectionModel(props.arrSelect ?? []);
  }, [props.arrSelect]);

  useEffect(() => {
    props.setArrSelect && props.setArrSelect(selectionModel);
  }, [selectionModel]);

  const getDeleteBtn = () => {
    return (
      <Box sx={{ display: "block", width: "100%" }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          sx={{ px: 2 }}
        >
          {selectionModel.length > 0 && props.onDelete !== undefined ? (
            <BtnDelete
              id="dl02"
              txt="ลบ"
              IsCircleWithOutText={false}
              onClick={() => props.onDelete(selectionModel)}
            />
          ) : null}
        </Stack>
      </Box>
    );
  };

  if (lstDefaultSorting == null) {
    let defaultData = {
      sSortDirection: props.rows.sSortDirection,
      sSortExpression: props.rows.sSortExpression,
    };
    setDefaultSorting(defaultData);
  }

  const handlePaginationChange = (value) => {
    if (!props.isLoading) {
      let cloneData = { ...props.rows, nPageIndex: value };
      if (props.handleDataMode === "client") {
        cloneData.nPageIndex -= 1;
      } else {
        cloneData.arrRows = [];
      }
      props.onLoadData(cloneData);
      window.scrollTo(0, 0);
    }
  };

  const handleJumpPageChange = (e) => {
    if (!props.isLoading) {
      let cloneData = { ...props.rows, nPageIndex: e.target.value };
      props.onLoadData(cloneData);
    }
  };

  // #region Menu Colums
  function MenuCloseComponent(props: GridColumnMenuItemProps) {
    return (
      <Button color="primary" onClick={props.onClick}>
        {`${I18n.SetText("closemenu")}`}
      </Button>
    );
  }
  function CustomColumnMenu(prop) {
    const itemProps = {
      colDef: prop.colDef,
      onClick: prop.hideMenu,
    };
    return (
      <React.Fragment>
        {itemProps.colDef.sortable ? (
          <Grid sx={{ zIndex: 1 }}>
            <Stack px={0.5} py={0.5}>
              <GridColumnMenuSortItem {...itemProps} />
              {props.isPin ? (
                <GridColumnMenuPinningItem {...itemProps} />
              ) : null}
            </Stack>
            <Divider />
            <Stack px={0.5} py={0.5}>
              <MenuCloseComponent {...itemProps} />
            </Stack>
          </Grid>
        ) : props.isPin ? (
          <GridColumnMenuPinningItem {...itemProps} />
        ) : null}
      </React.Fragment>
    );
  }
  // #endregion

  const CustomPagination = useCallback(() => {
    const nPage = props.rows.nPageIndex + (props.handleDataMode === "client" ? 1 : 0);

    const maxRowSizePerPage = nPage * props.rows.nPageSize;
    const rowTotalCount = props.rows.nDataLength;
    const minRowSizePerPage = nPage > 1 ? (nPage - 1) * props.rows.nPageSize + 1 : 1;
    const pageCount = Math.ceil(rowTotalCount / props.rows.nPageSize);
    const sumMaxRow = rowTotalCount > maxRowSizePerPage ? maxRowSizePerPage : rowTotalCount;
    let lstPage = [];
    for (let i = 0; i < pageCount; i++) {
      lstPage.push(<MenuItem value={i}>{i + 1}</MenuItem>);
    }

    return rowTotalCount < 1 || props.isHiddenPagination ? (
      getDeleteBtn()
    ) : (
      <Stack
        direction="row"
        sx={{ px: 2, minWidth: "550px" }}
        alignItems="center"
        flex={1}
        spacing={1}
      >
        <Stack flex={1} direction={"row"} spacing={1} alignItems={"center"}>
          {(selectionModel || []).length > 0 && props.onDelete !== undefined ? (
            <BtnDelete
              id="dl01"
              txt="ลบ"
              IsCircleWithOutText={false}
              onClick={() => props.onDelete(selectionModel)}
            />
          ) : null}
          {(selectionModel || []).length > 0 &&
            props.renderActionSelectRow != null
            ? props.renderActionSelectRow
            : null}
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ whiteSpace: "nowrap" }}>{"หน้า :"}</Typography>
          <Select
            size="small"
            disabled={props.isLoading}
            autoWidth
            sx={{
              height: 30,
              ".MuiOutlinedInput-notchedOutline > legend": {
                width: 0,
              },
            }}
            value={nPage}
            onChange={(e) => {
              handleJumpPageChange(e);
            }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 150 } } }}
          >
            {lstPage.map((item, index) => (
              <MenuItem key={item} value={index + 1}>
                {index + 1}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ whiteSpace: "nowrap" }}>{`${minRowSizePerPage > sumMaxRow ? sumMaxRow : minRowSizePerPage
            } - ${sumMaxRow} of ${rowTotalCount}`}</Typography>
          <Pagination
            color="primary"
            sx={{
              ".MuiPagination-ul": {
                flexWrap: "nowrap",
              },
            }}
            count={pageCount}
            siblingCount={0}
            boundaryCount={1}
            page={nPage}
            variant="outlined"
            shape="rounded"
            showFirstButton={true}
            showLastButton={true}
            size={"small"}
            onChange={(event, value) => {
              handlePaginationChange(value);
            }}
          />
          <Select
            label=""
            size="small"
            disabled={props.isLoading}
            autoWidth
            sx={{
              height: 30,
              ".MuiOutlinedInput-notchedOutline > legend": {
                width: 0,
              },
            }}
            value={props.lstPageSize.indexOf(props.rows.nPageSize)}
            onChange={(e) => {
              let cloneData = {
                ...props.rows,
                nPageIndex: 0,
                nPageSize: props.lstPageSize[e.target.value],
              };
              props.onLoadData(cloneData);
            }}
          >
            {props.lstPageSize.map((item, index) => (
              <MenuItem key={item} value={index}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>
    );
  }, [props.isLoading, selectionModel, props.rows]);

  const [isShowFilter, setShowFilter] = useState<boolean>(false);

  const handleSetValueDateRange = (item, form, sIDFilter) => {
    if (props.rows[item.sFieldName]) {
      form.setValue(sIDFilter, [
        props.rows[item.sFieldName][0]
          ? moment(props.rows[item.sFieldName][0])
          : null,
        props.rows[item.sFieldName][1]
          ? moment(props.rows[item.sFieldName][1])
          : null,
      ]);
    }
  };

  const handleSetValueDate = (item, form, sIDFilter) => {
    form.setValue(
      sIDFilter,
      props.rows[item.sFieldName] ? moment(props.rows[item.sFieldName]) : null
    );
  };

  const addFilterDateRange = (e, sIDFilter, cloneData, item) => {
    const data = e[sIDFilter];
    const typeData = typeof data;
    if (
      data &&
      data.length > 0 &&
      (data[0] || data[1]) &&
      typeData.toString() !== "undefined"
    ) {
      cloneData[item.sFieldName] = [
        data[0] ? moment(data[0]).valueOf() : null,
        data[1] ? moment(data[1]).valueOf() : moment(data[0]).valueOf(),
        // data[0] ? moment(data[0]).format("YYYY-MM-DD[T]00:00:00[.000Z]") : null,
        // data[1] ? moment(data[1]).format("YYYY-MM-DD[T]00:00:00[.000Z]") : null,
      ];
    }
    return cloneData;
  };

  const addFilterDate = (e, sIDFilter, cloneData, item) => {
    const data = e[sIDFilter];
    const typeData = typeof data;
    if (data && typeData.toString() !== "undefined") {
      cloneData[item.sFieldName] = moment(data).valueOf()

    }
    return cloneData;
  };

  const addFilterInput = (e, sIDFilter, cloneData, item) => {
    const data = e[sIDFilter];
    const typeData = typeof data;
    if (
      data &&
      typeData.toString() !== "undefined" &&
      data.toString().length > 0
    ) {
      cloneData[item.sFieldName] = data;
    }
    return cloneData;
  };

  const addFilterSelect = (e, sIDFilter, cloneData, item) => {
    const data = e[sIDFilter];
    const typeData = typeof data;
    if (data && data.length > 0 && typeData.toString() !== "undefined") {
      cloneData[item.sFieldName] = data;
    }
    return cloneData;
  };

  const onSearch = (e) => {
    let cloneData = {
      nPageSize: props.rows.nPageSize,
      nPageIndex: 1,
      sSortExpression: "",
      sSortDirection: "",
      rows: [],
    };
    if (props.filterField) {
      props.filterField.forEach((item) => {
        let sIDFilter = `filter_${props.sTableID}_${item.sFieldName}`;

        switch (item.sTypeFilterMode) {
          case "input": {
            addFilterInput(e, sIDFilter, cloneData, item);
            break;
          }
          case "select": {
            addFilterSelect(e, sIDFilter, cloneData, item);
            break;
          }
          case "multiselect": {
            addFilterSelect(e, sIDFilter, cloneData, item);
            break;
          }
          case "daterange": {
            cloneData = addFilterDateRange(e, sIDFilter, cloneData, item);
            break;
          }
          case "date": {
            cloneData = addFilterDate(e, sIDFilter, cloneData, item);
            break;
          }
        }
      });
    }

    // cloneData = {
    //   ...props.rows,
    //   sSortExpression: lstDefaultSorting.sSortExpression || "",
    //   sSortDirection: lstDefaultSorting.sSortDirection || "",
    //   arrRows: [],
    // };

    if (props.onLoadData) props.onLoadData(cloneData);
  };

  const CustomFilterPanel = (objFilter: ToolbarPropsOverrides) => {
    const form = useForm({
      shouldUnregister: false,
      shouldFocusError: true,
      mode: "all",
    });
    const offsets =
      document.getElementById("btn_filter_on_col")?.getBoundingClientRect() ??
      null;

    const _setValue = () => {
      props.filterField.forEach((item) => {
        let sIDFilter = `filter_${props.sTableID}_${item.sFieldName}`;
        switch (item.sTypeFilterMode) {
          case "input": {
            form.setValue(sIDFilter, props.rows[item.sFieldName]);
            break;
          }
          case "select": {
            form.setValue(sIDFilter, props.rows[item.sFieldName]);
            break;
          }
          case "multiselect": {
            form.setValue(sIDFilter, props.rows[item.sFieldName]);
            break;
          }
          case "daterange": {
            handleSetValueDateRange(item, form, sIDFilter);
            break;
          }
          case "date": {
            handleSetValueDate(item, form, sIDFilter);
            break;
          }
        }
      });
    };

    useMemo(() => {
      if (!props.isLoading) {
        !props.customFilterPanel && _setValue();
      }
    }, []);

    const onClear = () => {
      if (props.filterField) {
        props.filterField.forEach((item) => {
          let sIDFilter = `filter_${props.sTableID}_${item.sFieldName}`;
          form.setValue(sIDFilter, undefined);
        });
      }

      onSearch(form.getValues());
    };
    return (
      <div
        style={{
          maxHeight: 200,
          overflow: "auto",
          width: "100%",
          marginBottom: objFilter.isRow ? "1em" : "",
        }}
      >
        <Stack
          style={{ padding: "10px" }}
          direction={"row"}
          spacing={1}
          sx={
            objFilter.isRow
              ? {
                border: "1px #0168cc solid",
                mx: "1em",
                borderRadius: "10px",
                ":before": {
                  content: '""',
                  position: "absolute",
                  // left: offsets != null ? `${offsets.left - 10}px` : "4em",
                  left: "10em",
                  top: "2.5em",
                  borderLeft: "5px solid transparent",
                  borderRight: "5px solid transparent",
                  borderBottom: " 5px solid #0168cc",
                },
              }
              : null
          }
        >
          {props.customFilterPanel ? (
            props.customFilterPanel
          ) : (
            <FormProvider {...form}>
              <Grid
                container
                justifyContent={"end"}
                flexDirection={"row"}
                spacing={1}
              >
                {objFilter.filterField.map((item) => (
                  <Grid
                    key={item.sFieldName}
                    item
                    lg={item.nGridLG || undefined}
                    md={item.nGridMD || undefined}
                    sm={item.nGridSM || undefined}
                    xs={item.nGridXS || undefined}
                  >
                    {ControlFilter(
                      item,
                      objFilter.colums,
                      objFilter.isLoading,
                      objFilter.sTableID
                    )}
                  </Grid>
                ))}
              </Grid>
              <Grid sx={{ pt: "0.5%" }}>
                <Stack direction={"row"} spacing={1}>
                  <Tooltip title={I18n.SetText("Button.Clear", I18NextNs.labelComponent)}>
                    <Fab
                      sx={{ width: 40, height: 40, zIndex: 1 }}
                      color="default"
                      aria-label="clear"
                      onClick={onClear}
                    >
                      {/* <Clear /> */}
                      <RefreshRounded />
                    </Fab>
                  </Tooltip>
                  <Tooltip title={I18n.SetText("Button.Search", I18NextNs.labelComponent)}>
                    <Fab
                      type="submit"
                      sx={{ width: 40, height: 40, zIndex: 1 }}
                      color="primary"
                      aria-label="search"
                      onClick={form.handleSubmit(onSearch)}
                    >
                      <Search />
                    </Fab>
                  </Tooltip>
                </Stack>
              </Grid>
            </FormProvider>
          )}
        </Stack>
      </div>
    );
  };

  // #region Toolbar
  const CustomToolbar = (objToolbar: ToolbarPropsOverrides) => {
    return (
      <Stack direction={"column"}>
        <Stack
          className="head-container"
          sx={{
            px: 0.5,
            pb: 0.5,
            bgcolor: "white",
          }}
          direction="row"
          justifyContent="start"
        >
          <GridToolbarContainer>
            <Tooltip title={I18n.SetText("toolbarColumns")}>
              <GridToolbarColumnsButton />
            </Tooltip>
            {objToolbar.isFilter ? (
              <Tooltip title={I18n.SetText("filter")}>
                <GridToolbarFilterCustomButton
                  id="btn_filter_on_col"
                  onClick={() => {
                    objToolbar.setShowFilter(!objToolbar.isShowFilter);
                  }}
                >
                  <FilterAlt sx={{ marginRight: "0.2em" }} />
                  {I18n.SetText("filter")}
                </GridToolbarFilterCustomButton>
              </Tooltip>
            ) : null}
            {objToolbar.isExportExcel ? (
              <Tooltip title={"Export excel"}>
                <BtnExportExcel id="cd01" IsDisabled={objToolbar.isLoading} />
              </Tooltip>
            ) : null}
          </GridToolbarContainer>
        </Stack>
        {objToolbar.isShowFilter ? CustomFilterPanel(objToolbar) : null}
      </Stack>
    );
  };
  // #endregion
  return (
    <Box
      sx={{
        width: "100%",
        "& .remove-cell-padding": {
          padding: "0px !important",
        },
      }}
    >
      <DataGridPro
        defaultGroupingExpansionDepth={props.isDefaultGroupExpand ? -1 : 0}
        showCellVerticalBorder={props.isShowGridLine}
        sx={{
          "&.MuiDataGrid-root .MuiDataGrid-cell": {
            padding: '5px 10px',
          },
          "&.MuiDataGrid-root .MuiDataGrid-cell:focus": {
            //ปิด กรอบฟ้า ตอน Focus ช่องนั้นๆ
            outline: "none",
          },
          ".MuiDataGrid-columnHeaderTitleContainer": {
            border: "0 !important",
          },
          ".holiday-bg": {
            backgroundColor: "#c5c5c5"
          },
          " .MuiDataGrid-cell": {
            // color: "#333333",
            whiteSpace: "break-spaces !important",
            minHeight: "50px !important",
            verticalAlign: "top"
            // display: isAutoHeight ? "flex" : "flow-root"
            //  height:"max-content" 
            // backgroundColor: "white",
          },
        }}
        density="compact"
        autoHeight={true}
        rows={props.isLoading ? [] : props.rows?.arrRows || []}
        columns={props.columns}
        localeText={{
          columnMenuSortAsc: `${I18n.SetText("sortASC")}`,
          columnMenuSortDesc: `${I18n.SetText("sortDESC")}`,
          columnMenuUnsort: `${I18n.SetText("unsort")}`,
          unpin: `${I18n.SetText("unpin")}`,
          pinToLeft: `${I18n.SetText("pintoleft")}`,
          pinToRight: `${I18n.SetText("pintorigth")}`,
          toolbarColumns: `${I18n.SetText("toolbarColumns")}`,
          columnHeaderSortIconLabel: `${I18n.SetText(
            "columnHeaderSortIconLabel"
          )}`,
          columnMenuLabel: `${I18n.SetText("columnMenuLabel")}`,
          columnsPanelHideAllButton: `${I18n.SetText(
            "columnsPanelHideAllButton"
          )}`,
          columnsPanelShowAllButton: `${I18n.SetText(
            "columnsPanelShowAllButton"
          )}`,
          checkboxSelectionHeaderName: `${I18n.SetText(
            "checkboxSelectionHeaderNameColumn"
          )}`,
          columnsPanelTextFieldLabel: `${I18n.SetText(
            "columnsPanelTextFieldLabel"
          )}`,
          columnsPanelTextFieldPlaceholder: `${I18n.SetText(
            "columnsPanelTextFieldPlaceholder"
          )}`,
        }}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
          columnMenu: CustomColumnMenu,
          toolbar: props.isHiddenToolHead ? undefined : CustomToolbar,
          pagination: CustomPagination,
        }}
        slotProps={{
          filterPanel: {
            isRow: true,
            filterField: props.filterField,
            colums: props.columns,
            isLoading: false,
            sTableID: props.sTableID,
          },
          toolbar: {
            showQuickFilter: true,
            isRow: true,
            filterField: props.filterField,
            colums: props.columns,
            isLoading: false,
            sTableID: props.sTableID,
            isShowFilter: isShowFilter,
            setShowFilter: setShowFilter,
            isShowColomnTool: true,
            isExportExcel: false,
            isFilter: true,
          },
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: props.rows.nPageSize,
            },
          },
          pinnedColumns: props.arrPin ? { left: props.arrPin } : null
        }}
        rowCount={props.rows.nDataLength}
        disableMultipleColumnsSorting
        hideFooter={props.isHiddenFooter ? true : false}
        onSortModelChange={(model) => {
          if (model.length > 0) {
            let cloneData = {
              ...props.rows,
              sSortExpression: model[0].field,
              sSortDirection: model[0].sort,
              arrRows: [],
            };
            if (props.onLoadData) props.onLoadData(cloneData);
          } else {
            let cloneData = {
              ...props.rows,
              sSortExpression: lstDefaultSorting.sSortExpression || "",
              sSortDirection: lstDefaultSorting.sSortDirection || "",
              arrRows: [],
            };
            if (props.onLoadData) props.onLoadData(cloneData);
            // props.onLoadData(cloneData);
          }
        }}
        hideFooterSelectedRowCount
        checkboxSelection={props.isShowCheckBox}
        disableRowSelectionOnClick
        filterMode={props.handleDataMode}
        sortingMode={props.handleDataMode}
        paginationMode={props.handleDataMode}
        disableVirtualization={true}
        unstable_headerFilters={false}
        disableColumnFilter
        disableDensitySelector
        loading={props.isLoading}
        getRowId={(item) => {
          return item.sID;
        }}
        pagination
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(itm) => {
          setSelectionModel(itm);
          if (props.onSelectChange) props.onSelectChange(itm);
        }}
        onPaginationModelChange={(pageSize) => {
          let cloneData = {
            ...props.rows,
            nPageSize: pageSize,
            nPageIndex: 1,
            rows: [],
          };
          props.onLoadData(cloneData);
        }}
        isRowSelectable={props.onRowSelectable}
        onRowClick={props.onCickRow}
        experimentalFeatures={props.experimentalFeatures}
        columnGroupingModel={props.columnGroupingModel}
        treeData={props.isTreeData}
        getTreeDataPath={props.getTreeDataPath}
        groupingColDef={props.groupingColDef}
        getRowHeight={() => {
          return "auto";
        }}
        getDetailPanelContent={props.getDetailPanelContent}
        getDetailPanelHeight={props.getDetailPanelHeight}
        disableColumnMenu={props.isDisableColumnMenu}
      />
    </Box>
  );
}

export const initRows: PaginationInterface = {
  nPageIndex: 1,
  nPageSize: 10,
  arrRows: [],
  nDataLength: 0,
  sSortExpression: "",
  sSortDirection: "",
  sID: "",
};
export interface PaginationInterface {
  nPageIndex: number;
  nPageSize: number;
  arrRows: GridRowModel[];
  nDataLength: number;
  sSortExpression: string;
  sSortDirection: string;
  sID: string;
}

const defaultProp: DataGridProp = {
  rows: initRows,
  isLoading: false,
  isShowGridLine: true,
  isShowCheckBox: true,
  filterField: [],
  isDisableColumnMenu: true,
  lstPageSize: [10, 25, 50, 100],
  backgroundHeaderCustom: "#ff9800",
  maxRowNoScroll: 10,
  minHeightCustom: 500,
  isHiddenToolHead: false,
  isHiddenFooter: false,
  isHiddenPagination: false,
  handleDataMode: "server",
  sxCustomHeader: { color: "#3b3b3c" },
  isDisableColumnReorder: true,
  isExpand: false,
  isShowColomnTool: true,
  expandRowLength: 0,
  columns: [],
  onLoadData: () => { },
  listFixL: [],
  listFixR: [],
  sTableID: "tb1",
  isTreeData: false,
  getTreeDataPath: undefined,
  groupingColDef: undefined,
  customFilterPanel: undefined,
  onSelectChange: () => { },
  isPin: true,
  isSort: true,
};
DataGrid.defaultProps = defaultProp;
